/**
 * ProtectedRoute - Route guard component for authenticated routes
 * Redirects unauthenticated users to login page
 * Shows loading state during auth check to prevent content flash
 */

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useIsAuthenticated, useAuthHydrated, useAuthLoading } from "@/stores";
import { Spinner } from "@/components/ui/Spinner";

export interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Loading screen shown while checking auth state
 */
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen bg-[var(--cosmos)] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="lg" />
        <p className="text-[var(--frost-400)] text-sm">
          Checking authentication...
        </p>
      </div>
    </div>
  );
}

/**
 * Route guard that protects authenticated-only routes
 * Waits for auth store hydration before making redirect decisions
 *
 * @param children - The protected content to render
 * @param redirectTo - Where to redirect unauthenticated users (default: /login)
 */
export function ProtectedRoute({
  children,
  redirectTo = "/login",
}: ProtectedRouteProps) {
  const isAuthenticated = useIsAuthenticated();
  const isHydrated = useAuthHydrated();
  const isLoading = useAuthLoading();
  const location = useLocation();

  // Show loading while store hydrates from localStorage
  if (!isHydrated || isLoading) {
    return <AuthLoadingScreen />;
  }

  // Redirect unauthenticated users
  if (!isAuthenticated) {
    // Save the attempted URL for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Render protected content
  return <>{children}</>;
}
